/* BOJOURNAL — theme.js (core)
   Loaded on every page. Holds only what every page needs:
   the component lifecycle, overlays, header, reveal, fit-text,
   quantity inputs and Theme Editor wiring.

   Section behaviour lives in its own module and is loaded by the section
   that needs it: tabs.js, deferred-media.js, portal-hero.js, product.js,
   facets.js. Cart and predictive search are global (cart.js, search.js).

   Lifecycle rule for every component in this theme:
   all listeners are registered through this.on(), which binds them to an
   AbortController. disconnectedCallback aborts it, connectedCallback builds
   a fresh one — so Theme Editor reloads, reorders and removals can never
   leave duplicate or orphaned listeners behind. */
(() => {
  if (window.Bojournal && window.Bojournal.Component) return; // loaded twice: do nothing

  const Bojournal = (window.Bojournal = window.Bojournal || {});
  const FOCUSABLE =
    'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  Bojournal.FOCUSABLE = FOCUSABLE;
  Bojournal.reduceMotion = reduceMotion;
  Bojournal.define = (name, ctor) => {
    if (!customElements.get(name)) customElements.define(name, ctor);
  };
  Bojournal.announce = (message) => {
    const region = document.querySelector('[data-live-region]');
    if (!region) return;
    region.textContent = '';
    window.setTimeout(() => (region.textContent = message), 60);
  };
  /* Duration of a CSS transition token in ms, so JS never hard-codes timing. */
  Bojournal.duration = (token = '--dur-3') => {
    if (reduceMotion.matches) return 0;
    const raw = getComputedStyle(document.documentElement).getPropertyValue(token).trim();
    return parseFloat(raw) || 0;
  };

  /* ------------------------------------------------------------------
     Component base
     ------------------------------------------------------------------ */
  class Component extends HTMLElement {
    connectedCallback() {
      if (this._controller) this._controller.abort();
      this._controller = new AbortController();
      if (this.init) this.init();
    }

    disconnectedCallback() {
      if (this._controller) this._controller.abort();
      this._controller = null;
      if (this.teardown) this.teardown();
    }

    on(target, type, handler, options = {}) {
      if (!target) return;
      target.addEventListener(type, handler, { ...options, signal: this._controller.signal });
    }
  }
  Bojournal.Component = Component;

  /* ------------------------------------------------------------------
     Scroll lock without layout shift: the scrollbar width is given back
     as padding while the page is locked.
     ------------------------------------------------------------------ */
  const lock = {
    count: 0,
    on() {
      if (this.count++ > 0) return;
      const bar = window.innerWidth - document.documentElement.clientWidth;
      document.documentElement.style.setProperty('--scrollbar-w', `${bar}px`);
      document.body.classList.add('is-locked');
    },
    off() {
      this.count = Math.max(0, this.count - 1);
      if (this.count > 0) return;
      document.body.classList.remove('is-locked');
      document.documentElement.style.removeProperty('--scrollbar-w');
    }
  };
  Bojournal.scrollLock = lock;

  /* ------------------------------------------------------------------
     Paintings without their photo margin. Product photographs usually
     show a painting on a plain ground, sometimes with a logo or a line of
     text in a corner. artBox() finds the painting in a small CORS copy:
     it measures, column by column and then row by row, how much differs
     from the ground, and keeps the strongest continuous band — so a small
     mark in a corner is never mistaken for part of the work; the shadow the
     canvas casts on the ground counts as margin too. trimArt()
     then moves and scales the shown photo so the painting alone fills its
     frame, always at its own proportions (never stretched). When the image
     cannot be read across origins nothing changes.
     ------------------------------------------------------------------ */
  const artBox = (img) => {
    const size = 160;
    const k = size / Math.max(img.naturalWidth, img.naturalHeight);
    const w = Math.max(1, Math.round(img.naturalWidth * k));
    const h = Math.max(1, Math.round(img.naturalHeight * k));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(img, 0, 0, w, h);
    const data = ctx.getImageData(0, 0, w, h).data; // throws when not readable
    const at = (x, y) => (y * w + x) * 4;

    // The ground: the most common colour along the photo's edges, so a logo
    // or a line of text in a corner never decides it.
    const bins = new Map();
    const vote = (x, y) => {
      const i = at(x, y);
      const key = (data[i] >> 4) * 256 + (data[i + 1] >> 4) * 16 + (data[i + 2] >> 4);
      const bin = bins.get(key) || { n: 0, r: 0, g: 0, b: 0 };
      bin.n += 1;
      bin.r += data[i];
      bin.g += data[i + 1];
      bin.b += data[i + 2];
      bins.set(key, bin);
    };
    for (let x = 0; x < w; x += 1) {
      vote(x, 0);
      vote(x, 1);
      vote(x, h - 1);
      vote(x, h - 2);
    }
    for (let y = 2; y < h - 2; y += 1) {
      vote(0, y);
      vote(1, y);
      vote(w - 1, y);
      vote(w - 2, y);
    }
    const common = [...bins.values()].sort((m, n) => n.n - m.n)[0];
    const bg = [common.r / common.n, common.g / common.n, common.b / common.n];

    // off: differs from the ground. soft: differs, but only as a cast shadow
    // does — the ground's own colour with less light (the three channels
    // darkened by the same share).
    const off = new Uint8Array(w * h);
    const soft = new Uint8Array(w * h);
    for (let y = 0; y < h; y += 1) {
      for (let x = 0; x < w; x += 1) {
        const i = at(x, y);
        if (Math.abs(data[i] - bg[0]) + Math.abs(data[i + 1] - bg[1]) + Math.abs(data[i + 2] - bg[2]) > 36) {
          off[y * w + x] = 1;
          const r = data[i] / Math.max(bg[0], 1);
          const g = data[i + 1] / Math.max(bg[1], 1);
          const b = data[i + 2] / Math.max(bg[2], 1);
          if (Math.max(r, g, b) - Math.min(r, g, b) < 0.07 && (r + g + b) / 3 > 0.5) soft[y * w + x] = 1;
        }
      }
    }

    // The strongest continuous band of a profile; short dips (a pale stripe
    // inside the painting) do not break it.
    const band = (profile) => {
      const max = Math.max(...profile);
      if (!max) return null;
      const limit = max * 0.3;
      const gap = Math.max(2, Math.round(profile.length * 0.03));
      let best = null;
      let start = -1;
      let end = -1;
      let mass = 0;
      let quiet = 0;
      const close = () => {
        if (start >= 0 && (!best || mass > best.mass)) best = { a: start, b: end, mass };
        start = -1;
        mass = 0;
        quiet = 0;
      };
      profile.forEach((value, index) => {
        if (value >= limit) {
          if (start < 0) start = index;
          end = index;
          mass += value;
          quiet = 0;
        } else if (start >= 0) {
          quiet += 1;
          if (quiet > gap) close();
        }
      });
      close();
      return best;
    };
    const columns = (y0, y1) => {
      const out = new Array(w).fill(0);
      for (let y = y0; y <= y1; y += 1) for (let x = 0; x < w; x += 1) out[x] += off[y * w + x];
      return out;
    };
    const rows = (x0, x1) => {
      const out = new Array(h).fill(0);
      for (let y = 0; y < h; y += 1) for (let x = x0; x <= x1; x += 1) out[y] += off[y * w + x];
      return out;
    };
    let cols = band(columns(0, h - 1));
    if (!cols) return null;
    const lines = band(rows(cols.a, cols.b));
    if (!lines) return null;
    cols = band(columns(lines.a, lines.b)) || cols;

    // The shadow a canvas casts on the ground is margin, not painting: rows
    // and columns at the edge of the band that hold nothing but shadow are
    // peeled off. A wide pale area (over 8% of the side) is taken to be the
    // painting itself and is left alone.
    let x0 = cols.a;
    let x1 = cols.b;
    let y0 = lines.a;
    let y1 = lines.b;
    const shadowRow = (y) => {
      let n = 0;
      let s = 0;
      for (let x = x0; x <= x1; x += 1) {
        n += off[y * w + x];
        s += soft[y * w + x];
      }
      return n > 0 && s >= n * 0.9;
    };
    const shadowCol = (x) => {
      let n = 0;
      let s = 0;
      for (let y = y0; y <= y1; y += 1) {
        n += off[y * w + x];
        s += soft[y * w + x];
      }
      return n > 0 && s >= n * 0.9;
    };
    const peel = (isShadow, lo, hi, step) => {
      const limit = Math.max(1, Math.round((hi - lo + 1) * 0.08));
      let n = 0;
      let i = step > 0 ? lo : hi;
      while (i >= lo && i <= hi && n <= limit) {
        if (!isShadow(i)) break;
        n += 1;
        i += step;
      }
      return n > limit ? 0 : n;
    };
    y0 += peel(shadowRow, y0, y1, 1);
    y1 -= peel(shadowRow, y0, y1, -1);
    x0 += peel(shadowCol, x0, x1, 1);
    x1 -= peel(shadowCol, x0, x1, -1);

    // One pixel in from the edge, so no light line of margin survives.
    const minX = Math.min(x1, x0 + 1);
    const maxX = Math.max(minX, x1 - 1);
    const minY = Math.min(y1, y0 + 1);
    const maxY = Math.max(minY, y1 - 1);
    const box = { x: minX / w, y: minY / h, w: (maxX - minX + 1) / w, h: (maxY - minY + 1) / h };
    const share = box.w * box.h;
    if (share > 0.94 || share < 0.04) return null;
    box.ratio = (box.w * img.naturalWidth) / (box.h * img.naturalHeight);
    return box;
  };
  Bojournal.artBox = artBox;

  /* frame: the element that clips; shown: the <img> inside it, absolutely
     positioned at 0 0 and 100% × 100%. The painting covers the frame at its
     own proportions: when the frame is (almost) the painting's shape it
     fills it exactly; otherwise the painting is centred and the smallest
     part is cropped — the image is never stretched. done(box) runs once
     it is trimmed. */
  Bojournal.trimArt = (frame, shown, done) => {
    if (!frame || !shown || !window.HTMLCanvasElement || frame.hasAttribute('data-trimmed')) return;
    const src = shown.currentSrc || shown.src;
    if (!src) {
      shown.addEventListener('load', () => Bojournal.trimArt(frame, shown, done), { once: true });
      return;
    }
    const probe = new Image();
    probe.crossOrigin = 'anonymous';
    probe.decoding = 'async';
    probe.onload = () => {
      try {
        const box = artBox(probe);
        if (!box) return;
        const place = (frameRatio) => {
          const photo = probe.naturalWidth / probe.naturalHeight;
          let width;
          let height;
          let left;
          let top;
          if (box.ratio >= frameRatio) {
            // Wider than the frame: the painting's height fills it.
            height = 100 / box.h;
            width = (height * photo) / frameRatio;
            top = -box.y * height;
            left = 50 - (box.x + box.w / 2) * width;
          } else {
            width = 100 / box.w;
            height = (width * frameRatio) / photo;
            left = -box.x * width;
            top = 50 - (box.y + box.h / 2) * height;
          }
          shown.style.width = width.toFixed(3) + '%';
          shown.style.height = height.toFixed(3) + '%';
          shown.style.left = left.toFixed(3) + '%';
          shown.style.top = top.toFixed(3) + '%';
        };
        if (done) done(box);
        // The frame's shape after done() (which may give it the painting's).
        const fw = frame.offsetWidth;
        const fh = frame.offsetHeight;
        place(fw && fh ? fw / fh : box.ratio);
        frame.setAttribute('data-trimmed', '');
      } catch (error) {
        /* not readable across origins: the cropped photo stays */
      }
    };
    // A small copy is enough to find the painting.
    try {
      const url = new URL(src, window.location.href);
      if (url.searchParams.has('width')) url.searchParams.set('width', '240');
      probe.src = url.toString();
    } catch (error) {
      probe.src = src;
    }
  };

  /* ------------------------------------------------------------------
     Overlay base: mobile menu, search, cart drawer
     Dialog semantics, focus trap, Escape, scroll lock, focus return.
     ------------------------------------------------------------------ */
  class ModalPanel extends Component {
    init() {
      this.on(this, 'click', (event) => {
        if (event.target.closest('[data-modal-close]')) {
          event.preventDefault();
          this.close();
        }
      });
      this.on(this, 'keydown', (event) => {
        if (event.key === 'Escape' && this.isOpen) {
          event.stopPropagation();
          this.close();
        }
        if (event.key === 'Tab') this.trapFocus(event);
      });
    }

    teardown() {
      window.clearTimeout(this._finish);
      if (this.isOpen) lock.off();
      this._closing = false;
    }

    get isOpen() {
      return !this.hasAttribute('hidden');
    }

    open(trigger) {
      if (this.isOpen) return;
      // Opening from inside another panel (search link in the menu):
      // focus must later return to what opened that first panel.
      const host = trigger && trigger.closest ? trigger.closest('modal-panel, cart-drawer') : null;
      this.trigger = host && host !== this && host.trigger ? host.trigger : trigger || document.activeElement;
      ModalPanel.closeAll(this, { restoreFocus: false });
      window.clearTimeout(this._finish);
      this.removeAttribute('hidden');
      lock.on();
      if (this.trigger && this.trigger.hasAttribute && this.trigger.hasAttribute('aria-expanded')) {
        this.trigger.setAttribute('aria-expanded', 'true');
      }
      requestAnimationFrame(() => requestAnimationFrame(() => this.classList.add('is-open')));
      const target = this.querySelector('[autofocus], [data-predictive-input]') || this.querySelector(FOCUSABLE);
      window.setTimeout(() => target && target.focus({ preventScroll: true }), 40);
      this.dispatchEvent(new CustomEvent('panel:open', { bubbles: true }));
    }

    close({ restoreFocus = true } = {}) {
      if (!this.isOpen || this._closing) return;
      this._closing = true;
      this.classList.remove('is-open');
      this._finish = window.setTimeout(() => {
        this.setAttribute('hidden', '');
        this._closing = false;
        lock.off();
      }, Bojournal.duration('--dur-3') + 20);
      const trigger = this.trigger;
      if (trigger && trigger.hasAttribute && trigger.hasAttribute('aria-expanded')) {
        trigger.setAttribute('aria-expanded', 'false');
      }
      if (restoreFocus) {
        const fallback = document.querySelector(this.dataset.focusFallback || '[data-menu-open], .header__logo');
        const target = trigger && document.contains(trigger) && trigger.offsetParent !== null ? trigger : fallback;
        if (target) target.focus({ preventScroll: true });
      }
      this.dispatchEvent(new CustomEvent('panel:close', { bubbles: true }));
    }

    trapFocus(event) {
      const items = [...this.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    static closeAll(except, options) {
      document.querySelectorAll('modal-panel, cart-drawer').forEach((panel) => {
        if (panel !== except && panel.isOpen) panel.close(options);
      });
    }
  }

  Bojournal.define('modal-panel', ModalPanel);
  Bojournal.ModalPanel = ModalPanel;
  window.BojournalModalPanel = ModalPanel; // kept for modules written against v1

  /* One delegated listener for overlay triggers — survives section re-renders. */
  document.addEventListener('click', (event) => {
    const menuBtn = event.target.closest('[data-menu-open]');
    if (menuBtn) {
      const menu = document.getElementById('MobileMenu');
      if (menu && menu.open) menu.open(menuBtn);
      return;
    }
    const searchBtn = event.target.closest('[data-search-open]');
    if (searchBtn) {
      const search = document.getElementById('SearchOverlay');
      if (search && search.open) {
        event.preventDefault();
        search.open(searchBtn);
      }
    }
  });

  /* ------------------------------------------------------------------
     Header: transparent over the portal, solid everywhere else.
     A scroll listener (not IntersectionObserver) so jumps — anchors, End
     key, scroll restoration — never leave the header in the wrong state.
     ------------------------------------------------------------------ */
  class SiteHeader extends Component {
    init() {
      this.transparent = this.dataset.transparent === 'true';
      this.drops = [...this.querySelectorAll('[data-nav-drop]')];
      if (this.transparent) this.watchHero();
      this.setupDropdowns();
      this.watchCondense();
      requestAnimationFrame(() => this.setAttribute('data-ready', ''));
    }

    /* Once the visitor is reading (solid header, well past the top), the bar
       tightens on large screens: a slimmer band, a smaller mark. It never
       hides. The section keeps its full height, so nothing below moves. */
    watchCondense() {
      let ticking = false;
      const update = () => {
        ticking = false;
        const y = window.scrollY;
        const solid = this.hasAttribute('data-solid');
        const open = this.drops.some((d) => d.open);
        if (!solid || y < 60) this.toggleAttribute('data-condensed', false);
        else if (y > 160 && !open) this.toggleAttribute('data-condensed', true);
      };
      const request = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(update);
      };
      this.on(window, 'scroll', request, { passive: true });
      update();
    }

    setSolid(solid) {
      if (this.hasAttribute('data-solid') !== solid) this.toggleAttribute('data-solid', solid);
    }

    watchHero() {
      let ticking = false;
      const update = () => {
        ticking = false;
        if (this.drops.some((d) => d.open)) return;
        this.watchHeroState();
      };
      const request = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(update);
      };
      this.on(window, 'scroll', request, { passive: true });
      this.on(window, 'resize', request, { passive: true });
      this.on(document, 'portal:tone', (event) => {
        this.dataset.tone = event.detail.tone;
      });
      this.on(document, 'shopify:section:load', request);
      this.on(document, 'shopify:section:unload', () => window.setTimeout(request, 0));
      update();
    }

    watchHeroState() {
      const sentinel = document.querySelector('[data-header-sentinel]');
      // No portal at the top of the page: behave like every inner page.
      if (!sentinel) return this.setSolid(true);
      // A small hysteresis band: the state can never flutter back and forth
      // while the visitor scrolls around the edge of the portal.
      const top = sentinel.getBoundingClientRect().top;
      const h = this.offsetHeight;
      if (top < h) this.setSolid(true);
      else if (top > h + 24) this.setSolid(false);
    }

    setupDropdowns() {
      if (!this.drops.length) return;
      const canHover = window.matchMedia('(hover: hover) and (pointer: fine)');
      this.drops.forEach((drop) => {
        // The dropdown image follows the link under the pointer / focus.
        const images = [...drop.querySelectorAll('[data-drop-image]')];
        if (images.length) {
          const show = (event) => {
            const target = event.target.closest('[data-drop-target]');
            if (!target) return;
            const next = images.find((img) => img.dataset.dropImage === target.dataset.dropTarget);
            if (!next) return;
            images.forEach((img) => img.classList.toggle('is-active', img === next));
          };
          this.on(drop, 'mouseover', show);
          this.on(drop, 'focusin', show);
        }
        let timer;
        const summary = drop.querySelector('summary'); // <details> already exposes expanded state
        this.on(drop, 'toggle', () => {
          if (drop.open) {
            this.drops.forEach((other) => other !== drop && (other.open = false));
            this.setSolid(true);
          } else if (this.transparent && !this.drops.some((d) => d.open)) {
            this.watchHeroState();
          }
        });
        this.on(drop, 'mouseenter', () => {
          if (!canHover.matches) return;
          clearTimeout(timer);
          timer = setTimeout(() => (drop.open = true), 90);
        });
        this.on(drop, 'mouseleave', () => {
          if (!canHover.matches) return;
          clearTimeout(timer);
          timer = setTimeout(() => (drop.open = false), 180);
        });
        this.on(drop, 'keydown', (event) => {
          if (event.key === 'Escape' && drop.open) {
            drop.open = false;
            summary.focus();
          }
          // Arrow down from the summary moves into the panel.
          if (event.key === 'ArrowDown' && event.target === summary) {
            event.preventDefault();
            drop.open = true;
            const first = drop.querySelector('.nav-drop__panel a');
            if (first) requestAnimationFrame(() => first.focus());
          }
        });
        this.on(drop, 'focusout', (event) => {
          if (drop.open && event.relatedTarget && !drop.contains(event.relatedTarget)) drop.open = false;
        });
      });
      this.on(document, 'click', (event) => {
        this.drops.forEach((drop) => {
          if (drop.open && !drop.contains(event.target)) drop.open = false;
        });
      });
    }
  }

  Bojournal.define('site-header', SiteHeader);

  /* ------------------------------------------------------------------
     Quantity stepper. Respects min / max / step; never lets a value
     outside the inventory limit reach the form.
     ------------------------------------------------------------------ */
  class QuantityInput extends Component {
    init() {
      this.input = this.querySelector('input');
      if (!this.input) return;
      this.querySelectorAll('button[name="plus"], button[name="minus"]').forEach((button) => {
        this.on(button, 'click', () => {
          const previous = this.input.value;
          if (button.name === 'plus') this.input.stepUp();
          else this.input.stepDown();
          if (previous !== this.input.value) this.input.dispatchEvent(new Event('change', { bubbles: true }));
          this.sync();
        });
      });
      this.on(this.input, 'change', () => this.clamp());
      this.on(this.input, 'blur', () => this.clamp());
      this.sync();
    }

    clamp() {
      const min = Number(this.input.min || 0);
      const max = this.input.max ? Number(this.input.max) : Infinity;
      let value = Math.round(Number(this.input.value));
      if (Number.isNaN(value)) value = min;
      value = Math.min(Math.max(value, min), max);
      if (String(value) !== this.input.value) this.input.value = value;
      this.sync();
    }

    sync() {
      const value = Number(this.input.value);
      const min = Number(this.input.min || 0);
      const max = this.input.max ? Number(this.input.max) : Infinity;
      const minus = this.querySelector('[name="minus"]');
      const plus = this.querySelector('[name="plus"]');
      if (minus) minus.disabled = value <= min;
      if (plus) plus.disabled = value >= max;
    }
  }

  Bojournal.define('quantity-input', QuantityInput);

  /* ------------------------------------------------------------------
     Reading progress: a hairline across the top of the window that fills
     as the article body is read. Decorative (aria-hidden); follows the
     reading direction.
     ------------------------------------------------------------------ */
  class ReadingProgress extends Component {
    init() {
      this.bar = this.querySelector('.read-progress__bar');
      this.body = this.closest('article') && this.closest('article').querySelector('.article__body');
      if (!this.bar || !this.body) return;
      const update = () => {
        this._raf = 0;
        const rect = this.body.getBoundingClientRect();
        const total = rect.height - window.innerHeight * 0.6;
        const done = Math.min(1, Math.max(0, (window.innerHeight * 0.4 - rect.top) / Math.max(1, total)));
        this.bar.style.transform = 'scaleX(' + done.toFixed(4) + ')';
        this.classList.toggle('is-active', done > 0 && done < 1);
      };
      const queue = () => {
        if (!this._raf) this._raf = requestAnimationFrame(update);
      };
      this.on(window, 'scroll', queue, { passive: true });
      this.on(window, 'resize', queue);
      update();
    }
  }
  Bojournal.define('reading-progress', ReadingProgress);

  /* ------------------------------------------------------------------
     Reveal on scroll (gentle opacity + translation).
     One shared observer for the whole page.
     ------------------------------------------------------------------ */
  let revealObserver = null;
  const initReveal = (root = document) => {
    const items = root.querySelectorAll('[data-reveal]:not(.is-in)');
    const motionOn = document.body.dataset.motion === 'true' && !reduceMotion.matches;
    if (!motionOn || !('IntersectionObserver' in window)) {
      items.forEach((item) => item.classList.add('is-in'));
      return;
    }
    if (!revealObserver) {
      revealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-in');
            revealObserver.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -6% 0px', threshold: 0.04 }
      );
    }
    items.forEach((item) => {
      // Anything already above the fold appears without waiting.
      if (item.getBoundingClientRect().top < window.innerHeight * 0.9) item.classList.add('is-in');
      else revealObserver.observe(item);
    });
  };
  Bojournal.reveal = initReveal;
  window.BojournalReveal = initReveal;

  /* ------------------------------------------------------------------
     Oversized wordmark: fit exactly to its container.
     ------------------------------------------------------------------ */
  const fitText = (root = document) => {
    root.querySelectorAll('[data-fit-text]').forEach((el) => {
      const parent = el.parentElement;
      const style = getComputedStyle(parent);
      const available = parent.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
      el.style.display = 'inline-block';
      el.style.fontSize = '100px';
      const natural = el.scrollWidth;
      el.style.display = '';
      if (natural > 0) el.style.fontSize = `${Math.floor((available / natural) * 100 * 100) / 100}px`;
      el.classList.add('is-fitted');
    });
  };
  Bojournal.fitText = fitText;

  /* ------------------------------------------------------------------
     Small delegated behaviours (survive any re-render)
     ------------------------------------------------------------------ */
  /* Back to top: scroll home, then hand focus to the page content so a
     keyboard user continues from the top as well. */
  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-back-to-top]');
    if (!link) return;
    event.preventDefault();
    window.scrollTo({ top: 0, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    const main = document.getElementById('main');
    if (main) main.focus({ preventScroll: true });
  });

  /* "/" opens search from anywhere, as on most catalogues — never while
     typing in a field. */
  document.addEventListener('keydown', (event) => {
    if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey) return;
    const el = document.activeElement;
    if (el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName))) return;
    const search = document.getElementById('SearchOverlay');
    if (!search || typeof search.open !== 'function' || !search.hidden) return;
    event.preventDefault();
    search.open(document.querySelector('[data-search-open]'));
  });

  document.addEventListener('change', (event) => {
    const select = event.target.closest('[data-autosubmit]');
    if (select && select.form) select.form.submit();
  });

  /* ------------------------------------------------------------------
     Soft image arrival: lazy images inside media frames fade in once
     decoded. One capturing listener for the whole document.
     ------------------------------------------------------------------ */
  const markPending = (root = document) => {
    if (reduceMotion.matches) return;
    root.querySelectorAll('.media img[loading="lazy"]').forEach((img) => {
      if (!img.complete) img.classList.add('is-pending');
    });
  };
  document.addEventListener(
    'load',
    (event) => {
      if (event.target.tagName === 'IMG') event.target.classList.remove('is-pending');
    },
    true
  );
  document.addEventListener(
    'error',
    (event) => {
      if (event.target.tagName === 'IMG') event.target.classList.remove('is-pending');
    },
    true
  );

  /* ------------------------------------------------------------------
     Jump links: a shortcut whose target section is not on the page
     (hidden because it has no content) is removed, never left dead.
     ------------------------------------------------------------------ */
  const pruneJumps = (root = document) => {
    root.querySelectorAll('[data-jump-links]').forEach((list) => {
      list.querySelectorAll('a[href^="#"]').forEach((link) => {
        const id = decodeURIComponent(link.hash.slice(1));
        if (!id || !document.getElementById(id)) link.closest('li').remove();
      });
      if (!list.querySelector('li')) list.remove();
    });
  };

  /* ------------------------------------------------------------------
     Boot + Theme Editor
     ------------------------------------------------------------------ */
  const boot = () => {
    pruneJumps();
    markPending();
    initReveal();
    fitText();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => fitText());
    let resizeTimer;
    let lastWidth = window.innerWidth;
    window.addEventListener(
      'resize',
      () => {
        if (window.innerWidth === lastWidth) return; // mobile toolbars change height only
        lastWidth = window.innerWidth;
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => fitText(), 150);
      },
      { passive: true }
    );
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  document.addEventListener('shopify:section:load', (event) => {
    pruneJumps();
    markPending(event.target);
    initReveal(event.target);
    fitText(event.target);
  });

  // Editor selects a block inside an overlay or a closed disclosure: show it.
  document.addEventListener('shopify:block:select', (event) => {
    const block = event.target;
    const details = block.closest('details');
    if (details) details.open = true;
    block.querySelectorAll('[data-reveal]').forEach((el) => el.classList.add('is-in'));
  });

  // Leaving a section in the editor must never leave the page locked.
  document.addEventListener('shopify:section:unload', () => {
    window.setTimeout(() => {
      if (!document.querySelector('modal-panel:not([hidden]), cart-drawer:not([hidden])')) {
        lock.count = 0;
        lock.off();
      }
    }, 0);
  });
})();

/* ------------------------------------------------------------------
   The gallery experience — three small touches, each switched in Theme
   settings › Experience, none on touch screens or with reduced motion:
   1. A work's image travels from the clicked card into its product page
      (names the clicked image for the cross-document View Transition).
   2. Gallery light: a soft warm light follows the pointer over paintings.
   3. A round "View" label follows the pointer over paintings.
   ------------------------------------------------------------------ */
(() => {
  if (window.__bjExperience) return;
  window.__bjExperience = true;
  const body = document.body;
  const fine = window.matchMedia('(hover: hover) and (pointer: fine)');
  const still = window.matchMedia('(prefers-reduced-motion: reduce)');
  const WORKS = '.gwall__link, .card--artwork .card__link, .spotlight__wall, .fartist__work .card__link, .search-pick, .corridor__work';

  /* 0. Works are hung as they appear: each painting drops a little and
     settles, as if just placed on its nail. */
  if (body.hasAttribute('data-hang') && !still.matches && 'IntersectionObserver' in window) {
    const HANG = '.card--artwork .card__img:not(.card__img--alt), .gwall__item, .spotlight__wall img, .corridor__work';
    let batch = 0;
    let batchTimer = 0;
    const hangObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const node = entry.target;
        hangObserver.unobserve(node);
        node.style.setProperty('--hang-i', batch);
        batch += 1;
        window.clearTimeout(batchTimer);
        batchTimer = window.setTimeout(() => (batch = 0), 120);
        node.classList.add('is-hung');
        node.addEventListener('animationend', () => {
          node.classList.remove('hang', 'is-hung');
          node.style.removeProperty('--hang-i');
        }, { once: true });
      });
    }, { rootMargin: '0px 0px -6% 0px', threshold: 0.15 });
    const prepare = () => {
      document.querySelectorAll(HANG).forEach((node) => {
        if (node.hasAttribute('data-hang-ready')) return;
        node.setAttribute('data-hang-ready', '');
        node.classList.add('hang');
        hangObserver.observe(node);
      });
    };
    prepare();
    let queued = 0;
    new MutationObserver(() => {
      if (queued) return;
      queued = requestAnimationFrame(() => {
        queued = 0;
        prepare();
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  /* 1. Card → product page */
  const clearArt = () => {
    document.querySelectorAll('[data-vt-art]').forEach((img) => {
      img.style.viewTransitionName = '';
      img.removeAttribute('data-vt-art');
    });
  };
  document.addEventListener('click', (event) => {
    const link = event.target.closest && event.target.closest('a[href*="/products/"]');
    if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || link.target === '_blank') return;
    const img = link.querySelector('img');
    if (!img) return;
    clearArt();
    // On a product page its own main image already carries the name.
    document.querySelectorAll('.pdp__media-item.is-first .pdp__img').forEach((main) => {
      main.style.viewTransitionName = 'none';
      main.setAttribute('data-vt-art', '');
    });
    img.style.viewTransitionName = 'bj-art';
    img.setAttribute('data-vt-art', '');
  });
  window.addEventListener('pageshow', clearArt);

  if (!fine.matches || still.matches) return;

  /* 2. Gallery light */
  if (body.hasAttribute('data-gallery-light')) {
    let lit = null;
    let raf = 0;
    let pos = null;
    const paint = () => {
      raf = 0;
      if (!lit || !pos) return;
      const r = lit.getBoundingClientRect();
      lit.style.setProperty('--lx', (((pos.x - r.left) / r.width) * 100).toFixed(1) + '%');
      lit.style.setProperty('--ly', (((pos.y - r.top) / r.height) * 100).toFixed(1) + '%');
    };
    document.addEventListener('pointermove', (event) => {
      const target = event.target.closest && event.target.closest('.gwall__link, .card--artwork .card__media, .spotlight__wall, .pdp__media--artwork .pdp__zoom');
      if (target !== lit) {
        if (lit) lit.classList.remove('is-lit');
        lit = target;
        if (lit) lit.classList.add('is-lit');
      }
      pos = { x: event.clientX, y: event.clientY };
      if (lit && !raf) raf = requestAnimationFrame(paint);
    }, { passive: true });
  }

  /* 3. "View" label: a small light label that appears, still, on the
     lower edge of the painting under the pointer (no following cursor). */
  const label = body.getAttribute('data-view-cursor');
  if (label) {
    const tag = document.createElement('span');
    tag.className = 'view-tag';
    tag.setAttribute('aria-hidden', 'true');
    tag.innerHTML = '<span></span><svg viewBox="0 0 24 24" focusable="false"><path d="M7 17 17 7M9 7h8v8" fill="none" stroke="currentColor" stroke-width="1.4"/></svg>';
    tag.firstChild.textContent = label;
    body.appendChild(tag);
    let current = null;
    const frameOf = (work) => work.querySelector('.card__media, .search-pick__media') || work;
    const place = () => {
      if (!current) return;
      const r = frameOf(current).getBoundingClientRect();
      tag.style.left = (r.left + r.width / 2).toFixed(1) + 'px';
      tag.style.top = (r.bottom - 14).toFixed(1) + 'px';
    };
    const show = (work) => {
      current = work;
      place();
      tag.classList.add('is-on');
    };
    const hide = () => {
      current = null;
      tag.classList.remove('is-on');
    };
    document.addEventListener('pointerover', (event) => {
      const work = event.target.closest && event.target.closest(WORKS);
      if (work && work !== current && !event.target.closest('button, form, [data-no-view-cursor]')) show(work);
      else if (!work && current) hide();
    }, { passive: true });
    document.addEventListener('pointerleave', hide);
    window.addEventListener('scroll', () => current && place(), { passive: true });
    window.addEventListener('pagehide', hide);
  }
})();

/* ------------------------------------------------------------------
   <mono-nav>: the artist page's chapter bar. Hides chapters whose section
   is not on the page, marks the chapter being read, and knows when it is
   stuck under the header (for its hairline and the artist's name).
   ------------------------------------------------------------------ */
(() => {
  const B = window.Bojournal;
  if (!B || !B.Component || customElements.get('mono-nav')) return;

  class MonoNav extends B.Component {
    init() {
      this.links = [...this.querySelectorAll('a[href^="#"]')];
      this.targets = [];
      // Each chapter is the first section that declares it (data-chapter);
      // every link to it on the page is pointed at that section.
      // A chapter may span several sections (the works: corridor, selected,
      // all); the link leads to the first and stays marked through all.
      this.links.forEach((link) => {
        const chapter = link.getAttribute('href').slice(1);
        const sections = [...document.querySelectorAll('[data-chapter="' + chapter + '"]')];
        if (!sections.length) {
          link.parentElement.hidden = true;
          return;
        }
        const first = sections[0];
        if (!first.id) first.id = chapter;
        document.querySelectorAll('a[href="#' + chapter + '"]').forEach((a) => a.setAttribute('href', '#' + first.id));
        sections.forEach((target) => this.targets.push({ target, link }));
      });
      this.hidden = new Set(this.targets.map(({ link }) => link)).size < 2;
      if (this.hidden) return;

      // Other sticky parts (the corridor's stage) settle below this bar.
      const measure = () => document.documentElement.style.setProperty('--chapter-bar-h', this.offsetHeight + 'px');
      measure();
      this.on(window, 'resize', measure);

      const stuck = () => {
        const top = parseFloat(getComputedStyle(this).top) || 0;
        this.classList.toggle('is-stuck', this.getBoundingClientRect().top <= top + 1);
      };
      this.on(window, 'scroll', stuck, { passive: true });
      stuck();

      if (!('IntersectionObserver' in window)) return;
      const inView = new Map();
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => inView.set(entry.target, entry.isIntersecting));
        const current = this.targets.find(({ target }) => inView.get(target));
        this.links.forEach((link) => {
          if (current && link === current.link) link.setAttribute('aria-current', 'true');
          else link.removeAttribute('aria-current');
        });
      }, { rootMargin: '-35% 0px -55% 0px' });
      this.targets.forEach(({ target }) => this.observer.observe(target));
    }

    teardown() {
      if (this.observer) this.observer.disconnect();
      document.documentElement.style.removeProperty('--chapter-bar-h');
    }
  }

  B.define('mono-nav', MonoNav);
})();

/* ------------------------------------------------------------------
   Artist works: All · Available · Sold, without reloading.
   ------------------------------------------------------------------ */
(() => {
  if (window.__bjWorksFilter) return;
  window.__bjWorksFilter = true;
  const ready = () => document.querySelectorAll('[data-works-filter][hidden]').forEach((group) => (group.hidden = false));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', ready);
  else ready();
  document.addEventListener('click', (event) => {
    const button = event.target.closest && event.target.closest('[data-works-filter] [data-filter]');
    if (!button) return;
    const group = button.closest('[data-works-filter]');
    const list = group.parentElement.querySelector('.works-grid');
    if (!list) return;
    const mode = button.dataset.filter;
    group.querySelectorAll('[data-filter]').forEach((b) => b.setAttribute('aria-pressed', String(b === button)));
    [...list.children].forEach((item) => {
      const available = item.dataset.available === 'true';
      item.hidden = (mode === 'available' && !available) || (mode === 'sold' && available);
      if (!item.hidden) item.querySelectorAll('[data-reveal]').forEach((card) => card.classList.add('is-in'));
    });
  });
})();

/* ------------------------------------------------------------------
   Quick view — a work opened in a side panel from its card. The panel's
   content is the product's own "quick-view" section, fetched through the
   Section Rendering API (?section_id=quick-view), so price, availability
   and the add-to-cart form are always live. Fetched once per work, and
   early when the pointer rests on the button. If anything fails, the
   work's page opens instead.
   ------------------------------------------------------------------ */
(() => {
  if (window.__bjQuickView) return;
  window.__bjQuickView = true;
  const cache = new Map();
  let token = 0;

  const load = (url) => {
    if (!cache.has(url)) {
      const address = new URL(url, window.location.href);
      address.searchParams.set('section_id', 'quick-view');
      const request = fetch(address.toString(), { headers: { Accept: 'text/html' } })
        .then((response) => {
          if (!response.ok) throw new Error(String(response.status));
          return response.text();
        })
        .catch((error) => {
          cache.delete(url);
          throw error;
        });
      cache.set(url, request);
    }
    return cache.get(url);
  };

  const SKELETON =
    '<div class="qv qv--loading" aria-busy="true">' +
    '<div class="qv__media"><span class="qv__stage qv__ghost"></span></div>' +
    '<div class="qv__info"><span class="qv__ghost qv__ghost--s"></span><span class="qv__ghost qv__ghost--l"></span>' +
    '<span class="qv__ghost qv__ghost--m"></span><span class="qv__ghost qv__ghost--btn"></span></div></div>';

  document.addEventListener('click', async (event) => {
    const trigger = event.target.closest && event.target.closest('[data-quick-view]');
    if (!trigger) return;
    const url = trigger.dataset.quickView;
    const drawer = document.getElementById('QuickView');
    const body = drawer && drawer.querySelector('[data-qv-body]');
    event.preventDefault();
    if (!drawer || !drawer.open || !body || !window.fetch || !window.DOMParser) {
      window.location.href = url;
      return;
    }
    const mine = (token += 1);
    drawer.setAttribute('aria-labelledby', 'QuickViewLabel');
    body.innerHTML = SKELETON;
    drawer.open(trigger);
    try {
      const html = await load(url);
      if (mine !== token) return;
      const content = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-qv]');
      if (!content) throw new Error('empty');
      body.replaceChildren(document.importNode(content, true));
      body.scrollTop = 0;
      if (body.querySelector('#QuickViewTitle')) drawer.setAttribute('aria-labelledby', 'QuickViewTitle');
    } catch (error) {
      if (mine !== token) return;
      const note = document.createElement('p');
      note.className = 'qv__fail t-small t-muted';
      note.textContent = drawer.dataset.error || '';
      body.replaceChildren(note);
      window.setTimeout(() => {
        window.location.href = url;
      }, 900);
    }
  });

  // Intent: the pointer resting on the button starts the fetch.
  document.addEventListener('pointerover', (event) => {
    const trigger = event.target.closest && event.target.closest('[data-quick-view]');
    if (trigger && window.fetch) load(trigger.dataset.quickView).catch(() => {});
  }, { passive: true });

  // Other views of the work.
  document.addEventListener('click', (event) => {
    const thumb = event.target.closest && event.target.closest('[data-qv-thumb]');
    if (!thumb) return;
    const root = thumb.closest('[data-qv]');
    const main = root && root.querySelector('.qv__img');
    if (main) {
      main.srcset = thumb.dataset.srcset;
      main.src = thumb.dataset.src;
    }
    root.querySelectorAll('[data-qv-thumb]').forEach((button) => button.setAttribute('aria-pressed', String(button === thumb)));
  });

  // A work in several versions (sizes, frames): price and button follow.
  document.addEventListener('change', (event) => {
    const select = event.target.closest && event.target.closest('[data-qv-variant]');
    if (!select) return;
    const option = select.selectedOptions[0];
    const root = select.closest('[data-qv]');
    if (!option || !root) return;
    const available = option.dataset.available === 'true';
    const price = root.querySelector('.qv__price');
    if (price) {
      const current = document.createElement('span');
      current.className = available ? 'price__current' : 'price__sold';
      // The price is the theme's own markup (it may carry the riyal symbol).
      if (available) current.innerHTML = option.dataset.price;
      else current.textContent = option.dataset.sold;
      price.replaceChildren(current);
      price.classList.toggle('price--sold', !available);
    }
    const add = root.querySelector('[data-add-button]');
    if (add) {
      add.disabled = !available;
      const label = add.querySelector('[data-add-label]');
      if (label) label.textContent = available ? add.dataset.labelAdd : add.dataset.labelSold;
    }
  });
})();

/* ------------------------------------------------------------------
   Saved works — a heart on every work; the list lives on this device
   (localStorage), opens from the header, and can be sent on WhatsApp.
   Every [data-save] button on the page mirrors the list, including ones
   added later by section re-renders.
   ------------------------------------------------------------------ */
(() => {
  if (window.__bjSaved) return;
  window.__bjSaved = true;
  const KEY = 'bj-saved';
  const read = () => {
    try {
      const list = JSON.parse(window.localStorage.getItem(KEY) || '[]');
      return Array.isArray(list) ? list : [];
    } catch (error) {
      return [];
    }
  };
  let memory = read();
  const write = (list) => {
    memory = list;
    try {
      window.localStorage.setItem(KEY, JSON.stringify(list));
    } catch (error) {
      /* storage unavailable: the list lasts for this page */
    }
  };
  const has = (handle) => memory.some((item) => item.handle === handle);
  const esc = (text) => String(text || '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  const renderDrawer = () => {
    const drawer = document.getElementById('SavedDrawer');
    if (!drawer) return;
    const list = drawer.querySelector('[data-saved-list]');
    const empty = drawer.querySelector('[data-saved-empty]');
    const foot = drawer.querySelector('[data-saved-foot]');
    const removeLabel = drawer.dataset.remove || 'Remove [title]';
    list.innerHTML = memory
      .map(
        (item) =>
          '<li class="saved-item">' +
          '<a class="saved-item__media" href="' + esc(item.url) + '" tabindex="-1" aria-hidden="true">' +
          (item.image ? '<img src="' + esc(item.image) + '" alt="" width="120" height="120" loading="lazy">' : '') +
          '</a>' +
          '<a class="saved-item__title" href="' + esc(item.url) + '">' + esc(item.title) + '</a>' +
          '<button class="saved-item__remove" type="button" data-saved-remove="' + esc(item.handle) + '" aria-label="' + esc(removeLabel.replace('[title]', item.title)) + '">' +
          '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6 6 18" fill="none" stroke="currentColor" stroke-width="1.4"/></svg>' +
          '</button></li>'
      )
      .join('');
    if (empty) empty.hidden = memory.length > 0;
    if (foot) foot.hidden = memory.length === 0;
    const share = drawer.querySelector('[data-saved-share]');
    if (share) {
      const origin = drawer.dataset.origin || window.location.origin;
      const lines = memory.map((item) => '• ' + item.title + ' — ' + origin + item.url);
      const text = (drawer.dataset.intro || '') + '\n' + lines.join('\n');
      const number = drawer.dataset.wa || '';
      share.href = 'https://wa.me/' + number + '?text=' + encodeURIComponent(text);
    }
  };

  const sync = () => {
    document.querySelectorAll('[data-save]').forEach((button) => {
      button.setAttribute('data-save-synced', '');
      const on = has(button.dataset.save);
      button.setAttribute('aria-pressed', String(on));
      const label = button.querySelector('[data-save-label]');
      if (label) label.textContent = on ? button.dataset.labelOn : button.dataset.labelOff;
    });
    document.querySelectorAll('[data-saved-count]').forEach((count) => {
      count.textContent = memory.length;
      count.hidden = memory.length === 0;
    });
    document.querySelectorAll('.header__tool--saved').forEach((tool) => tool.classList.toggle('has-items', memory.length > 0));
    document.querySelectorAll('.header__tool--menu').forEach((tool) => tool.classList.toggle('has-saved', memory.length > 0));
    renderDrawer();
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-save]');
    if (button) {
      event.preventDefault();
      const handle = button.dataset.save;
      if (has(handle)) {
        write(memory.filter((item) => item.handle !== handle));
      } else {
        write([{ handle, url: button.dataset.url, title: button.dataset.title, image: button.dataset.image }, ...memory].slice(0, 60));
        button.classList.remove('is-popping');
        void button.offsetWidth;
        button.classList.add('is-popping');
        document.querySelectorAll('.header__tool--saved').forEach((tool) => {
          tool.classList.remove('is-bumped');
          void tool.offsetWidth;
          tool.classList.add('is-bumped');
        });
      }
      sync();
      return;
    }
    const remove = event.target.closest('[data-saved-remove]');
    if (remove) {
      write(memory.filter((item) => item.handle !== remove.dataset.savedRemove));
      sync();
      const drawer = document.getElementById('SavedDrawer');
      const next = drawer && drawer.querySelector('[data-saved-remove], [data-modal-close]');
      if (next) next.focus();
      return;
    }
    const opener = event.target.closest('[data-saved-open]');
    if (opener) {
      const drawer = document.getElementById('SavedDrawer');
      if (drawer && drawer.open) {
        renderDrawer();
        drawer.open(opener);
      }
    }
  });

  window.addEventListener('storage', (event) => {
    if (event.key === KEY) {
      memory = read();
      sync();
    }
  });

  // Buttons added later (filters, recommendations, section reloads).
  let queued = 0;
  new MutationObserver(() => {
    if (queued || !document.querySelector('[data-save]:not([data-save-synced])')) return;
    queued = requestAnimationFrame(() => {
      queued = 0;
      sync();
    });
  }).observe(document.documentElement, { childList: true, subtree: true });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', sync);
  else sync();
})();
